// First import. Its body runs before App pulls in TON derivation, which
// calls Buffer while the module is still loading.
import './buffer-polyfill';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, hydrate, dehydrate } from '@tanstack/react-query';
import { App } from './App';
import { dbg, dbgActiveFlags, dbgFlag } from './lib/debug';
import { telegramUserId } from './hooks/useTelegram';
import { readWebSession } from './lib/webSession';
import './i18n';
import './index.css';

// TEMPORARY diagnostics: catch a JS boot (a reload increments this), uncaught
// errors, promise rejections, and page unloads — so we can tell on-device whether
// the "gray screen → back to main" is a reload/crash or an in-app close.
dbg(`BOOT [safe:${dbgActiveFlags()}]`);
window.addEventListener('error', (e) =>
  dbg(`ERROR ${e.message || ''} @${(e.filename || '').split('/').pop() || ''}:${e.lineno || ''}`),
);
window.addEventListener('unhandledrejection', (e) =>
  dbg(`REJECT ${String((e as PromiseRejectionEvent).reason).slice(0, 140)}`),
);
window.addEventListener('pagehide', () => dbg('PAGEHIDE (unload/reload)'));

// TEMPORARY safe-mode bisect: kill EVERY backdrop-filter (the liquid-glass nav,
// centre disc, selection lens, card action buttons, frozen overlay, sheets).
// iOS WebKit has a hard cap on stacked backdrop-filter/compositing layers; the
// cards screen (cards + nav glass + disc + lens + buttons) can blow past it and
// blank the whole WebView. Toggle "liquid glass" in Profile → Diagnostics: if
// the white screen disappears with this ON, the glass layers are the cause.
if (dbgFlag('noglass')) {
  const s = document.createElement('style');
  s.textContent =
    '*{backdrop-filter:none!important;-webkit-backdrop-filter:none!important;}';
  document.head.appendChild(s);
  dbg('SAFE noglass applied');
}

// NO JavaScript keyboard handling. On iOS Telegram natively resizes the webview
// to end at the keyboard top when an input focuses, so bottom-anchored UI (the
// card sheets) clears the keyboard by itself. Do NOT reintroduce visualViewport
// height math / --kb-inset lifts / scrollIntoView / rect patches /
// `interactive-widget=resizes-content` — each was tried and caused double-lifts,
// gaps, blinking or hard WebView reloads (see BottomSheet.tsx).
//
// The one thing we DO enforce: the document is position:fixed and must never
// scroll. Telegram-iOS's scrollToActiveElement / WebKit's focus autoscroll can
// still pan the WKWebView's scroll view to "reveal" a focused input, which
// shifts the whole fixed UI off-screen. Pin the scroll back to 0 the moment
// anything moves it.
const pinScroll = () => {
  if (
    window.scrollY === 0 &&
    document.documentElement.scrollTop === 0 &&
    document.body.scrollTop === 0
  ) {
    return;
  }
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};
window.addEventListener('scroll', pinScroll);
window.visualViewport?.addEventListener('scroll', pinScroll);

// Outside Telegram, keep the shell sized to the visible viewport so a
// retracting mobile URL bar doesn't leave a gray html band under the page.
//
// Home-screen "Open as Web App" (display-mode: standalone / iOS
// navigator.standalone) has no URL bar — do NOT chase visualViewport.height.
// When the keyboard opens, vv shrinks by hundreds of px; shrinking #root with
// it collapses the layout and the iOS Paste/AutoFill callout anchors to the
// wrong caret rect (overlaps the address field on Send).
//
// In a regular Safari tab, still ignore keyboard-sized shrinks (~150px+):
// only URL-bar jitter should update --tg-viewport-stable-height.
if (!window.Telegram?.WebApp?.initData) {
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    // iOS Safari home-screen web app (pre-manifest / legacy).
    ('standalone' in navigator &&
      (navigator as Navigator & { standalone?: boolean }).standalone === true);

  if (isStandalone) {
    document.documentElement.setAttribute('data-standalone', '');
    const h = Math.round(window.innerHeight || window.visualViewport?.height || 0);
    if (h > 0) {
      document.documentElement.style.setProperty('--tg-viewport-stable-height', `${h}px`);
    }
    // Full device height for html/body background (status bar + bottom dead
    // band). Do not apply this to #root — that lifted the tab bar.
    const screenH = Math.round(window.screen.height || h);
    if (screenH > 0) {
      document.documentElement.style.setProperty('--tg-screen-height', `${screenH}px`);
    }
  } else {
    let lastStable = Math.round(window.visualViewport?.height ?? window.innerHeight);
    const syncShellHeight = () => {
      const h = Math.round(window.visualViewport?.height ?? window.innerHeight);
      if (h <= 0) return;
      // Keyboard open: large drop. Keep the pre-keyboard shell height.
      if (h < lastStable - 150) return;
      lastStable = h;
      document.documentElement.style.setProperty('--tg-viewport-stable-height', `${h}px`);
    };
    syncShellHeight();
    window.visualViewport?.addEventListener('resize', syncShellHeight);
    window.addEventListener('resize', syncShellHeight);
  }
}

const CACHE_KEY = 'incpt-query-cache';
/** Last wallet + balances only. The full query cache can fail to save (size,
 *  a non-JSON value) — this snapshot is what paints coins on the next open. */
const BALANCE_SNAP_KEY = 'incpt-balance-snap';
const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? 'dev';
const MAX_AGE = 24 * 60 * 60 * 1_000;

// localStorage is shared across all Telegram accounts on the device (same webview
// origin), so the persisted cache MUST be scoped to the current user — otherwise
// switching accounts hydrates the previous account's cards/balances and shows
// them until a refetch replaces them (the "wrong cards for a few seconds" bug).
// Unknown id (Telegram not finished injecting) is NOT a different user: wiping
// the cache in that case made every open wait on the live balance RPC.
const currentUserId = telegramUserId();
const hasWebSession = currentUserId == null && !!readWebSession();

type BalanceSnap = {
  userId: number;
  walletId: string;
  wallet: unknown;
  balances: unknown;
  ts: number;
};

function readBalanceSnap(): BalanceSnap | null {
  try {
    // This cache is keyed by Telegram numeric id. Never hydrate it in a web
    // session, where the server user id is only known after /me resolves.
    if (hasWebSession) return null;
    const raw = window.localStorage.getItem(BALANCE_SNAP_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as BalanceSnap;
    if (!snap?.walletId || typeof snap.userId !== 'number' || !snap.wallet || !snap.balances) {
      return null;
    }
    if (Date.now() - snap.ts > MAX_AGE) return null;
    if (currentUserId != null && snap.userId !== currentUserId) return null;
    return snap;
  } catch {
    return null;
  }
}

function writeBalanceSnap(client: QueryClient) {
  const userId = telegramUserId() ?? currentUserId;
  if (userId == null) return;
  const walletEntry = client.getQueryCache().find({ queryKey: ['wallet', userId] });
  const walletData = walletEntry?.state.data as { wallet?: { id?: string } | null } | undefined;
  const walletId = walletData?.wallet?.id;
  if (!walletId || walletEntry?.state.status !== 'success') return;
  const balancesEntry = client.getQueryCache().find({
    queryKey: ['balances', walletId, userId],
  });
  if (!balancesEntry || balancesEntry.state.status !== 'success' || balancesEntry.state.data == null) {
    return;
  }
  const snap: BalanceSnap = {
    userId,
    walletId,
    wallet: walletData,
    balances: balancesEntry.state.data,
    ts: Date.now(),
  };
  window.localStorage.setItem(BALANCE_SNAP_KEY, JSON.stringify(snap));
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      gcTime: MAX_AGE,
    },
  },
});

// Hydrate from localStorage BEFORE React renders so the very first frame already
// has cached data — no blank flash, no isRestoring delay. Only hydrate when the
// cache belongs to the SAME Telegram user; a different account starts clean.
try {
  const raw = window.localStorage.getItem(CACHE_KEY);
  if (hasWebSession) {
    // Prevent a browser session from briefly showing the last Telegram/web
    // account before /me supplies the current server user id.
    window.localStorage.removeItem(CACHE_KEY);
    window.localStorage.removeItem(BALANCE_SNAP_KEY);
  } else if (raw) {
    const { buster, userId, timestamp, clientState } = JSON.parse(raw) as {
      buster: string;
      userId: number | null;
      timestamp: number;
      clientState: unknown;
    };
    const savedUserId = userId ?? null;
    // Only a known, different account is a switch. A missing id at boot used to
    // delete the cache and force a cold balance fetch (~seconds of RPC).
    const differentUser =
      currentUserId != null && savedUserId != null && savedUserId !== currentUserId;
    if (differentUser) {
      window.localStorage.removeItem(CACHE_KEY);
      window.localStorage.removeItem(BALANCE_SNAP_KEY);
    } else if (buster === APP_VERSION && Date.now() - timestamp < MAX_AGE) {
      hydrate(queryClient, clientState);
    }
  }
} catch {
  // Corrupted cache — ignore, a fresh fetch will overwrite it.
}

// Coins paint from this even when the full query cache above was skipped.
const balanceSnap = readBalanceSnap();
if (balanceSnap) {
  queryClient.setQueryData(['wallet', balanceSnap.userId], balanceSnap.wallet);
  queryClient.setQueryData(
    ['balances', balanceSnap.walletId, balanceSnap.userId],
    balanceSnap.balances,
  );
}

// Persist cache changes back to localStorage (throttled to avoid hammering).
let saveTimer: ReturnType<typeof setTimeout> | null = null;
queryClient.getQueryCache().subscribe(() => {
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    const userId = telegramUserId() ?? currentUserId;
    try {
      writeBalanceSnap(queryClient);
    } catch {
      // Snapshot is best-effort; the live fetch still updates the screen.
    }
    try {
      const clientState = dehydrate(queryClient, {
        shouldDehydrateQuery: (query) => {
          if (query.state.status !== 'success') return false;
          // BTC spend plans contain BigInt and used to abort the whole save.
          // Candles are small (~60×5 numbers) — keep them so charts survive
          // a cold start / upstream outage with the last good sparkline.
          const head = query.queryKey[0];
          if (head === 'btcSpendQuote') return false;
          if (head === 'market-candles') {
            const data = query.state.data;
            return Array.isArray(data) && data.length >= 2;
          }
          return true;
        },
      });
      window.localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          buster: APP_VERSION,
          userId,
          timestamp: Date.now(),
          clientState,
        }),
      );
    } catch {
      // Storage full or unavailable — the balance snapshot above still stands.
    }
  }, 1_000);
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
